import React from 'react';
import CommonModal from '../../ui/CommonModal';
import { SignupBonusGrant, SignupBonusStatus } from '../../../types';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { inactivityRuleText, SIGNUP_BONUS_STATUS_LABEL } from '../signupBonus/signupBonusDisplay';

/**
 * Read-only detail of a player's welcome bonus.
 * Single quest: event P&L (peak sticky + live for floor). Deposit is not a quest.
 */
interface Props {
  bonus: SignupBonusGrant | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_STYLE: Record<SignupBonusStatus, { label: string; className: string }> = {
  inactive: { label: SIGNUP_BONUS_STATUS_LABEL.inactive, className: 'bg-violet-500/15 text-violet-800 ring-1 ring-violet-500/25' },
  active: { label: SIGNUP_BONUS_STATUS_LABEL.active, className: 'bg-emerald-500/15 text-emerald-800 ring-1 ring-emerald-500/25' },
  in_review: { label: SIGNUP_BONUS_STATUS_LABEL.in_review, className: 'bg-amber-500/15 text-amber-900 ring-1 ring-amber-500/25' },
  completed: { label: SIGNUP_BONUS_STATUS_LABEL.completed, className: 'bg-sky-500/15 text-sky-800 ring-1 ring-sky-500/25' },
  expired: { label: SIGNUP_BONUS_STATUS_LABEL.expired, className: 'bg-slate-500/10 text-slate-700 ring-1 ring-slate-500/20' },
};

const money = (v?: string | number | null) => `$${(Number(v ?? 0)).toFixed(2)}`;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-foreground tabular-nums text-right">{children}</span>
  </div>
);

const SignupBonusDetailModal: React.FC<Props> = ({ bonus, isOpen, onClose }) => {
  const fmt = useFormatDate();
  if (!bonus) return null;

  const st = STATUS_STYLE[bonus.status] || STATUS_STYLE.active;
  const peak = Number(bonus.peakNetWin ?? 0);
  const threshold = Number(bonus.winThreshold ?? 0);
  const pct = threshold > 0 ? Math.min(100, Math.max(0, (peak / threshold) * 100)) : 0;
  const grant = Number(bonus.grantedAmount ?? 0);
  const live = bonus.netWin != null ? Number(bonus.netWin) : null;
  const floorLifted =
    bonus.floorLiftedByLoss === true
    || (live != null && grant > 0 && live <= -grant + 1e-8);

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Welcome bonus"
      description="Event P&L since claim — not all-time game P&L. Read-only."
    >
      <div className="space-y-5">
        {/* 1. Status + event peak progress */}
        <section className="space-y-2.5">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${st.className}`}>
              {st.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {bonus.netWinMet || bonus.status === 'completed' || bonus.status === 'in_review' ? 'Event P&L ✓' : 'Event P&L —'}
            </span>
          </div>
          <div>
            <div className="mb-1 flex justify-between text-xs text-muted-foreground">
              <span>Event peak P&L</span>
              <span className="tabular-nums">{money(bonus.peakNetWin)} / {money(bonus.winThreshold)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${bonus.netWinMet || bonus.status === 'completed' || bonus.status === 'in_review' ? 'bg-emerald-500' : 'bg-sky-500'}`}
                style={{ width: `${bonus.netWinMet || bonus.status === 'completed' || bonus.status === 'in_review' ? 100 : pct}%` }}
              />
            </div>
          </div>
        </section>

        {/* 2. Amounts — P&L only */}
        <section className="rounded-lg border border-border/60 bg-muted/20 p-3">
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amounts</h4>
          <Row label="Granted (bonus credits)">{money(bonus.grantedAmount)}</Row>
          <Row label="Event P&L threshold">{money(bonus.winThreshold)}</Row>
          <Row label="Event peak P&L">{money(bonus.peakNetWin)}</Row>
          {live != null && <Row label="Event live P&L">{money(live)}</Row>}
          {bonus.status === 'active' && (
            <Row label="Payout floor">
              {floorLifted
                ? 'Lifted (event live P&L ≤ −grant)'
                : `Keep ≥ ${money(bonus.grantedAmount)} while active`}
            </Row>
          )}
          {bonus.status === 'in_review' && (
            <Row label="Payout gate">No floor — every payout needs manual approval until first approve</Row>
          )}
          {(bonus.status === 'completed' || bonus.status === 'in_review') && (
            <Row label="Settled grant">{money(bonus.realAmount)}</Row>
          )}
        </section>

        {/* 3. Time + source */}
        <section className="rounded-lg border border-border/60 bg-muted/20 p-3">
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Timeline &amp; source</h4>
          <Row label="Claimed at">{bonus.grantedAt ? fmt(bonus.grantedAt) : 'Not claimed yet'}</Row>
          <Row label="Inactivity rule">{inactivityRuleText(bonus)}</Row>
          {bonus.completedAt && <Row label="Completed at">{fmt(bonus.completedAt)}</Row>}
          {bonus.expiredAt && <Row label="Expired at">{fmt(bonus.expiredAt)}</Row>}
          <Row label="Funding source">{bonus.fundingSource === 'shop' ? 'Shop credits' : bonus.fundingSource === 'platform' ? 'Platform credits' : (bonus.fundingSource || '—')}</Row>
          {bonus.settlementReason && <Row label="Settlement reason">{bonus.settlementReason}</Row>}
        </section>

        {/* 4. Technical (collapsed) */}
        <details className="rounded-lg border border-border/60 bg-muted/10 p-3 text-sm">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Technical
          </summary>
          <div className="mt-2">
            <Row label="Bonus ID">
              <span className="font-mono text-xs">{bonus.id}</span>
            </Row>
            <Row label="Baseline balance">{money(bonus.baselineBalance)}</Row>
            <Row label="Stored expiresAt (legacy)">{bonus.expiresAt ? fmt(bonus.expiresAt) : '—'}</Row>
            <Row label="net_win_met">{String(bonus.netWinMet)}</Row>
          </div>
        </details>
      </div>
    </CommonModal>
  );
};

export default SignupBonusDetailModal;
